import {
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Eye, Ellipsis, ListTodo, NotebookText, Pencil, Plus, Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConversationalGuideSection } from './ConversationalGuide/ConversationalGuideSection';
import { PreviewModal } from './Preview/PreviewModal';
import { PublishedStatusBadge } from '../navbar/PublishedStatusBadge';
import {
  TranslationCoverageBadge,
  TranslationCoverageBadgeSkeleton,
  type ResourceCoverageLike,
} from '../components/translations/TranslationCoverageBadge';
import { TRANSLATION_POLL_INTERVAL_MS } from '../components/translations/coverageStatus';
import { UnitTranslationsToolbarPill } from '../components/translations/UnitTranslationsToolbarPill';
import { UnitGuidePreview } from './Preview/UnitGuidePreview';
import { AssessmentPreview } from './Preview/AssessmentPreview';
import { LearningMaterialPreview } from './Preview/LearningMaterialPreview';
import { UnitsOfCompetencySettingsDrawer } from './SettingsDrawer';
import { unitsOfCompetencyState } from './UnitsOfCompetencyState';
import { UnitStatsSection } from './UnitStatsSection';
import { withPermission } from '../withPermission';
import { initialConversationFlowContent } from './initialConversationFlowContent';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { EditorLayout } from '../navbar/EditorLayout';
import { EditorBreadcrumbs } from '../navbar/EditorBreadcrumbs';
import { EditableResourceTitle } from '../navbar/EditableResourceTitle';

export const UnitsOfCompetencyEditPage = withPermission('canManageCourseContent', () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { unitId } = useParams();
  if (!unitId) {
    throw new Error('unitId is required');
  }

  const [data, queryResult] = trpcAdmin.unitsOfCompetency.byId.useSuspenseQuery(unitId);
  const isLoading = !data || queryResult.isLoading;

  const { data: stats, isLoading: isLoadingStats } =
    trpcAdmin.unitsOfCompetency.getUnitStats.useQuery(unitId);

  // Translation coverage per learning resource, fetched in one batch so each row
  // can show a coverage badge beside its publish badge. Course-agnostic surface:
  // measured against every translatable language (a unit is shared across courses
  // with differing language sets, so there's no single course set here).
  const coverageResources = (data?.learningResources ?? [])
    .filter((resource) => resource.resource_id !== null)
    .map((resource) => ({
      resourceId: resource.resource_id,
      resourceKind:
        resource.resource_kind === 'PAGE' ? ('PAGE' as const) : ('CONVERSATION' as const),
    }));
  const { data: coverage, isSuccess: coverageLoaded } =
    trpcAdmin.contentTranslations.listByResources.useQuery(
      { resources: coverageResources },
      {
        enabled: coverageResources.length > 0,
        // Coverage rarely changes between visits to a unit, so serve it from cache
        // and don't refetch on every mount; the poll below catches in-flight work.
        staleTime: 30_000,
        refetchInterval: (query) => {
          const hasPending =
            query.state.data?.resources.some((resource) =>
              resource.rows.some((row) => row.status === 'PENDING'),
            ) ?? false;
          return hasPending ? TRANSLATION_POLL_INTERVAL_MS : false;
        },
      },
    );
  const coverageByResourceId = new Map<string, ResourceCoverageLike>(
    (coverage?.resources ?? []).map((resource) => [
      resource.resourceId,
      { currentPublishedVersionId: resource.currentPublishedVersionId, rows: resource.rows },
    ]),
  );

  if (isLoading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  if (!data) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" h="100%">
        <Trans>No unit found</Trans>
      </Flex>
    );
  }

  const { unitAssessment, learningResources } = data;

  const availableResourceKinds: string[] = [
    ...new Set(learningResources.map((resource) => resource.resource_kind)),
  ];

  const pageResource = learningResources.find((resource) => resource.resource_kind === 'PAGE');
  const conversationalResources = learningResources.filter(
    (resource) => resource.resource_kind === 'CONVERSATION',
  );

  const createResource = trpcAdmin.resources.create.useMutation({
    onSuccess: () => {
      toast({
        title: t`Learning material added successfully.`,
        status: 'success',
        isClosable: true,
      });
    },
    onSettled: (data) => {
      // after creating a resource, navigate to the resource page
      if (data?.id) {
        navigate(`/resources/${data.id}`);
      }
    },
  });

  const createAssessment = trpcAdmin.unitAssessment.create.useMutation({
    onSuccess: () => {
      toast({
        title: t`Assessment added successfully.`,
        status: 'success',
        isClosable: true,
      });
      navigate(`/units/${unitId}/assessment`);
    },
  });

  const updateUnit = trpcAdmin.unitsOfCompetency.update.useMutation({
    onSuccess: () => {
      void queryResult.refetch();
    },
  });

  // The title shown by both the toolbar field and the body heading, kept in one
  // piece of state so the two never disagree. Seeded from the query and re-synced
  // when the server title changes, but never while the user is mid-rename.
  const [displayTitle, setDisplayTitle] = useState(data.title);
  const isRenamingRef = useRef(false);
  useEffect(() => {
    if (!isRenamingRef.current) {
      setDisplayTitle(data.title);
    }
  }, [data.title]);

  // Single rename path shared by the toolbar title and the body heading. Optimistic
  // (keeps the typed value on screen), rolls back on failure, reconciled by the
  // refetch.
  //
  // `unitsOfCompetency.update` is a full replace (it resets unprovided fields to
  // defaults), so the rename passes the unit's current values back unchanged and
  // only swaps the title. A dedicated title-only rename endpoint would be cleaner.
  const renameUnit = async (value: string) => {
    const previousTitle = displayTitle;
    setDisplayTitle(value);
    isRenamingRef.current = true;
    try {
      await updateUnit.mutateAsync({
        id: data.id,
        title: value,
        code: data.code,
        description: data.description,
        work_placement: data.work_placement,
        nominalHours: data.nominalHours,
        volumeOfLearningHours: data.volumeOfLearningHours,
        subjectFieldOfEducation: data.subjectFieldOfEducation,
        vetFlag: data.vetFlag,
        unitContextCategoryId: data.unitContextCategoryId,
      });
    } catch (error) {
      setDisplayTitle(previousTitle);
      throw error;
    } finally {
      isRenamingRef.current = false;
    }
  };

  const previewModal = useDisclosure();
  const [previewType, setPreviewType] = useState<
    'learningMaterial' | 'unitGuide' | 'assessment' | null
  >(null);
  const [previewResourceId, setPreviewResourceId] = useState<string | null>(null);

  const openPreview = (
    type: 'learningMaterial' | 'unitGuide' | 'assessment',
    resourceId?: string,
  ) => {
    setPreviewType(type);
    setPreviewResourceId(resourceId ?? null);
    previewModal.onOpen();
  };

  const handleCreateConversationalResource = () => {
    createResource.mutate({
      title: `${data.code} - Conversational Guide - Section ${conversationalResources.length + 1}`,
      kind: 'CONVERSATION',
      content: initialConversationFlowContent,
      unitOfCompetencyId: data.id,
      position: conversationalResources.length,
    });
  };

  return (
    <EditorLayout
      toolbar={{
        breadcrumbs: (
          <EditorBreadcrumbs
            crumbs={[{ label: t`Units`, to: '/units' }]}
            current={
              <EditableResourceTitle
                title={displayTitle}
                onRename={renameUnit}
                ariaLabel={t`Unit title`}
              />
            }
          />
        ),
        actions: [
          {
            id: 'translations',
            node: <UnitTranslationsToolbarPill unitId={unitId} />,
          },
          {
            id: 'settings',
            node: (
              <Tooltip label={t`Settings`}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t`Settings`}
                  onClick={unitsOfCompetencyState.openSettingsDrawer}
                >
                  <Settings size={iconSize.md} color="currentColor" />
                </IconButton>
              </Tooltip>
            ),
          },
        ],
      }}
      body={{
        kind: 'content',
        children: (
          <>
            {/* The prominent title heading is kept on the page (not only in the
                toolbar breadcrumb) and edits through the same rename path, so the
                two stay in sync. */}
            <Flex flexDirection="column">
              <Text textStyle="xl" fontWeight="medium" color="fg.muted">
                {data.code}
              </Text>
              <Editable
                value={displayTitle}
                onChange={setDisplayTitle}
                onSubmit={(value) => {
                  void renameUnit(value);
                }}
                fontSize="3xl"
                fontWeight="medium"
              >
                <EditablePreview />
                <EditableInput />
              </Editable>
            </Flex>

            {/* Unit Analytics */}
            <UnitStatsSection stats={stats} isLoading={isLoadingStats} />

            {/* learning materials (conversational guide) */}
            <ListCard
              cardTitle={t`Learning material`}
              cardSubtitle={
                availableResourceKinds.includes('CONVERSATION')
                  ? t`Learning materials added to this unit.`
                  : t`No learning material in this unit.`
              }
              cardPrimaryAction={
                <IconButton
                  aria-label={t`Add conversational learning material`}
                  icon={<Icon as={Plus} color="fg.default" boxSize={iconSize.xl} />}
                  variant="ghost"
                  size="sm"
                  onClick={handleCreateConversationalResource}
                  isLoading={createResource.isPending}
                />
              }
              cardContent={
                availableResourceKinds.includes('CONVERSATION') ? (
                  <ConversationalGuideSection
                    data={conversationalResources}
                    onPreview={(resourceId) => openPreview('learningMaterial', resourceId)}
                    coverageByResourceId={coverageByResourceId}
                    coverageLoading={!coverageLoaded}
                  />
                ) : null
              }
            />
            {/* unit guide */}
            <ListCard
              cardTitle={t`Unit Guide`}
              cardSubtitle={
                availableResourceKinds.includes('PAGE')
                  ? t`Unit guide added to this unit.`
                  : t`No unit guide in this unit.`
              }
              cardPrimaryAction={
                <IconButton
                  aria-label={t`Add unit guide`}
                  icon={<Icon as={Plus} color="fg.default" boxSize={iconSize.xl} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    createResource.mutate({
                      title: `${data.code} - Unit Guide`,
                      kind: 'PAGE',
                      content: {
                        type: 'doc',
                        content: [
                          {
                            type: 'dBlock',
                            content: [
                              {
                                type: 'heading',
                                attrs: {
                                  level: 1,
                                },
                                content: [],
                              },
                            ],
                          },
                        ],
                      },
                      unitOfCompetencyId: data.id,
                    });
                  }}
                  isLoading={createResource.isPending}
                  hidden={availableResourceKinds.includes('PAGE')}
                />
              }
              cardContent={
                pageResource ? (
                  <HStack
                    p={2}
                    borderWidth="1px"
                    borderColor="border.emphasis"
                    borderRadius="md"
                    bg="bg.canvas"
                    gap={4}
                    w="full"
                    justify="start"
                  >
                    <HStack
                      gap={4}
                      align="center"
                      w="full"
                      flex="1"
                      onClick={() => {
                        if (pageResource.resource_id !== null) {
                          navigate(`/resources/${pageResource.resource_id}`);
                        }
                      }}
                      cursor="pointer"
                    >
                      <Icon
                        as={NotebookText}
                        color="fg.emphasis"
                        boxSize={pxToRem(32)}
                        strokeWidth="1px"
                      />
                      <Text fontSize="lg" lineHeight="7" fontWeight="medium" color="fg.default">
                        {pageResource.resource.title}
                      </Text>
                    </HStack>
                    <HStack gap={2}>
                      <PublishedStatusBadge
                        isPublished={pageResource.resource.last_published_version_id != null}
                      />
                      {pageResource.resource_id !== null ? (
                        coverageLoaded ? (
                          <TranslationCoverageBadge
                            coverage={
                              coverageByResourceId.get(pageResource.resource_id) ?? {
                                currentPublishedVersionId: null,
                                rows: [],
                              }
                            }
                          />
                        ) : (
                          <TranslationCoverageBadgeSkeleton />
                        )
                      ) : null}
                      <HStack gap={0}>
                        <Tooltip label={t`Preview`} closeOnClick>
                          <IconButton
                            aria-label={t`Preview`}
                            icon={<Icon as={Eye} />}
                            size="sm"
                            variant="tertiary"
                            _hover={{ color: 'brand.600' }}
                            _active={{ color: 'brand.600', bg: 'bg.accent.canvas' }}
                            onClick={(e) => {
                              e.currentTarget.blur();
                              openPreview('unitGuide', pageResource.resource_id ?? undefined);
                            }}
                          />
                        </Tooltip>
                        <Menu gutter={12}>
                          <MenuButton
                            as={IconButton}
                            aria-label={t`More options`}
                            size="sm"
                            variant="ghost"
                            icon={<Ellipsis size={iconSize.lg} />}
                          />
                          <MenuList fontSize="md">
                            <MenuItem
                              icon={<Pencil size={iconSize.md} />}
                              onClick={() => {
                                if (pageResource.resource_id !== null) {
                                  navigate(`/resources/${pageResource.resource_id}`);
                                }
                              }}
                            >
                              {t`Edit`}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </HStack>
                  </HStack>
                ) : null
              }
            />
            {/* assessment */}
            <ListCard
              cardTitle={t`Assessment`}
              cardSubtitle={
                unitAssessment ? t`Assessment added to this unit.` : t`No assessment in this unit.`
              }
              cardPrimaryAction={
                !unitAssessment ? (
                  <IconButton
                    aria-label={t`Add assessment`}
                    icon={<Icon as={Plus} color="fg.default" boxSize={iconSize.xl} />}
                    variant="ghost"
                    size="sm"
                    isLoading={createAssessment.isPending}
                    onClick={async () => {
                      createAssessment.mutate({
                        unitOfCompetencyId: unitId,
                      });
                    }}
                  />
                ) : undefined
              }
              cardContent={
                unitAssessment !== null ? (
                  <HStack
                    p={2}
                    borderWidth="1px"
                    borderColor="border.emphasis"
                    borderRadius="md"
                    bg="bg.canvas"
                    gap={4}
                    w="full"
                    justify="start"
                  >
                    <HStack
                      gap={4}
                      align="center"
                      w="full"
                      flex="1"
                      onClick={() => {
                        if (unitAssessment !== null) {
                          navigate(`/units/${unitId}/assessment`);
                        }
                      }}
                      cursor="pointer"
                    >
                      <Icon
                        as={ListTodo}
                        color="fg.emphasis"
                        boxSize={pxToRem(32)}
                        strokeWidth="1px"
                      />
                      <Text fontSize="lg" lineHeight="7" fontWeight="medium" color="fg.default">
                        {data.title} - {t`Assessment`}
                      </Text>
                    </HStack>
                    <HStack gap={2}>
                      <PublishedStatusBadge
                        isPublished={unitAssessment.lastPublishedVersionId != null}
                      />
                      <HStack gap={0}>
                        <Tooltip label={t`Preview`} closeOnClick>
                          <IconButton
                            aria-label={t`Preview`}
                            icon={<Icon as={Eye} />}
                            size="sm"
                            variant="tertiary"
                            _hover={{ color: 'brand.600' }}
                            _active={{ color: 'brand.600', bg: 'bg.accent.canvas' }}
                            onClick={(e) => {
                              e.currentTarget.blur();
                              openPreview('assessment');
                            }}
                          />
                        </Tooltip>
                        <Menu gutter={12}>
                          <MenuButton
                            as={IconButton}
                            aria-label={t`More options`}
                            size="sm"
                            variant="ghost"
                            icon={<Ellipsis size={iconSize.lg} />}
                          />
                          <MenuList fontSize="md">
                            <MenuItem
                              icon={<Pencil size={iconSize.md} />}
                              onClick={() => {
                                if (unitAssessment !== null) {
                                  navigate(`/units/${unitId}/assessment`);
                                }
                              }}
                            >
                              {t`Edit`}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </HStack>
                  </HStack>
                ) : null
              }
            />
          </>
        ),
      }}
    >
      <UnitsOfCompetencySettingsDrawer
        data={data}
        refetch={queryResult.refetch}
        courses={data.courseItem.map((item) => item.course).filter((course) => course != null)}
      />

      {/* -- Preview modal ----------------------------------------- */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={previewModal.onClose}
        title={t`${data.code} - ${data.title}`}
        hasPublishedVersion={
          previewType === 'unitGuide'
            ? pageResource?.resource.last_published_version_id !== null &&
              pageResource?.resource.last_published_version_id !== undefined
            : previewType === 'assessment'
              ? unitAssessment?.lastPublishedVersionId !== null &&
                unitAssessment?.lastPublishedVersionId !== undefined
              : previewResourceId !== null &&
                conversationalResources.some(
                  (r) =>
                    r.resource_id === previewResourceId &&
                    r.resource.last_published_version_id !== null,
                )
        }
      >
        {({ version, language: languageControls, versionToggle }) => {
          if (previewType === 'unitGuide' && pageResource?.resource_id) {
            return (
              <UnitGuidePreview
                resourceId={pageResource.resource_id}
                lastPublishedVersionId={pageResource.resource.last_published_version_id}
                version={version}
                languageControls={languageControls}
                versionToggle={versionToggle}
              />
            );
          }

          if (previewType === 'assessment' && unitAssessment) {
            // Assessments are always in English (the assess-in-English boundary),
            // so they take no preview language.
            return (
              <AssessmentPreview
                unitId={unitId}
                lastPublishedVersionId={unitAssessment.lastPublishedVersionId}
                version={version}
                versionToggle={versionToggle}
              />
            );
          }

          if (previewType === 'learningMaterial' && previewResourceId) {
            const resource = conversationalResources.find(
              (r) => r.resource_id === previewResourceId,
            );
            return (
              <LearningMaterialPreview
                resourceId={previewResourceId}
                lastPublishedVersionId={resource?.resource.last_published_version_id ?? null}
                version={version}
                languageControls={languageControls}
                versionToggle={versionToggle}
              />
            );
          }

          return null;
        }}
      </PreviewModal>
    </EditorLayout>
  );
});
